import { CreditPackage } from '@talon/web/lib/api';
import { Box, Button, Card, CardBody, CardFooter, CardHeader, Layer, Spinner, Text } from 'grommet';
import { Close } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTalentManagerStore } from '../../context';

interface Props {
  engagementId: string;
  hourlyRateInPennies: number;
  onClose: () => void;
}

const PackageCard = ({
  pkg,
  hourlyRateInPennies,
  selected,
  onSelect,
}: {
  pkg: CreditPackage;
  hourlyRateInPennies: number;
  selected: boolean;
  onSelect: () => void;
}) => {
  const totalCents = pkg.hours * hourlyRateInPennies;
  return (
    <Box
      background={selected ? 'accent-1' : 'light-2'}
      pad="medium"
      round="small"
      border={{ color: selected ? 'brand' : 'light-4', size: 'small' }}
      onClick={onSelect}
      hoverIndicator
      focusIndicator={false}
      margin={{ bottom: 'xsmall' }}
      direction="row"
      justify="between"
      align="center"
    >
      <Box>
        <Text weight="bold">{pkg.name}</Text>
        <Text size="small">{pkg.hours} hours of Talent Manager time</Text>
      </Box>
      <Box align="end">
        <Text size="large" weight="bold">
        ${(totalCents / 100).toFixed(0)}
        </Text>
        <Text size="xsmall" color="dark-4">
        ${(hourlyRateInPennies / 100).toFixed(0)}/hr
        </Text>
      </Box>
    </Box>
  );
};

export const CreditPackageModal = observer(({ engagementId, hourlyRateInPennies, onClose }: Props) => {
  const tmStore = useTalentManagerStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    tmStore.fetchCreditPackages();
  }, []);

  const handleCheckout = async () => {
    if (!selectedId) return;
    setRedirecting(true);
    const url = await tmStore.createCheckout(selectedId, engagementId, `${window.location.origin}${window.location.pathname}`);
    if (url) {
      window.location.href = url;
    } else {
      setRedirecting(false);
    }
  };

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} modal>
      <Card width="medium" background="light-1">
        <CardHeader pad="medium" justify="between" direction="row">
          <Text weight="bold" size="large">
            Buy Credits
          </Text>
          <Close onClick={onClose} cursor="pointer" />
        </CardHeader>
        <CardBody pad="medium">
          {tmStore.creditPackages.length === 0 ? (
            <Box align="center" pad="medium">
              <Spinner />
            </Box>
          ) : (
            tmStore.creditPackages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                hourlyRateInPennies={hourlyRateInPennies}
                selected={selectedId === pkg.id}
                onSelect={() => setSelectedId(pkg.id)}
              />
            ))
          )}
          <Text size="xsmall" color="dark-4" margin={{ top: 'small' }}>
            Credits are billed weekly. Unused minutes carry forward.
          </Text>
        </CardBody>
        <CardFooter pad="medium" justify="end" gap="small" direction="row">
          <Button label="Cancel" onClick={onClose} />
          <Button
            primary
            label={redirecting ? 'Redirecting…' : 'Proceed to Checkout'}
            onClick={handleCheckout}
            disabled={!selectedId || redirecting}
          />
        </CardFooter>
      </Card>
    </Layer>
  );
});
